import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { initEcwidWidget, handoffToEcwid, WIDGET_ID } from "@/lib/ecwidCheckout";

// Branded checkout page — the Ecwid widget initializes directly into the
// inline slot below and scrolls natively with the page, like a classic store.
export default function Checkout() {
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const ecwid = await initEcwidWidget();
        // Keep the widget locked to the checkout flow: if a link inside the
        // widget (cart product title, breadcrumbs, "back to store") lands on
        // Ecwid's default catalog pages, bounce straight back to the bag.
        if (!window.__mvmEcwidGuard) {
          window.__mvmEcwidGuard = true;
          ecwid.OnPageLoaded?.add?.((page) => {
            if (page && (page.type === "CATEGORY" || page.type === "PRODUCT" || page.type === "SEARCH")) {
              if (!window.location.hash.includes("~/cart")) window.location.hash = "!/~/cart";
              try {
                ecwid.openPage("cart");
              } catch {
                /* hashbang above already routes the widget */
              }
            }
          });
        }
        const lines = window.__mvmPendingCheckout || [];
        window.__mvmPendingCheckout = null;
        const warning = await handoffToEcwid(ecwid, lines);
        if (!cancelled) {
          setStatus("ready");
          if (warning) toast.warning(warning);
        }
      } catch (e) {
        if (!cancelled) {
          setStatus("error");
          toast.error(e.message || "Couldn't start checkout. Please try again.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <main data-testid="checkout-page" className="pt-32 md:pt-44 pb-24 min-h-screen">
      <div className="max-w-5xl mx-auto px-6 md:px-10">
        <p className="text-xs tracking-[0.3em] uppercase text-mist">Secure checkout</p>
        <h1 className="font-display text-5xl md:text-7xl font-semibold uppercase tracking-tight mt-4 leading-[0.95]">
          Finish strong.
        </h1>
        <p className="text-stone/55 text-sm mt-5 mb-10 max-w-md leading-relaxed">
          Review your bag and check out securely — payment, shipping and taxes are handled by the official MVM
          store via Ecwid.
        </p>
        {status === "loading" && (
          <p data-testid="checkout-loading" className="text-xs tracking-[0.3em] uppercase text-stone/40 mb-6 animate-pulse">
            Preparing your bag…
          </p>
        )}
        {status === "error" && (
          <p data-testid="checkout-error" className="text-sm text-red-400/90 mb-6">
            The secure checkout couldn't load. Check your connection and try again.
          </p>
        )}
        <div id={WIDGET_ID} data-testid="ecwid-checkout-slot" className="min-h-[420px]" />
        <div className="mt-12">
          <Link
            to="/shop"
            data-testid="checkout-continue-shopping"
            className="inline-block border border-stone/30 px-8 py-3 font-display uppercase tracking-[0.2em] text-sm hover:bg-stone hover:text-obsidian transition-colors"
          >
            Continue shopping
          </Link>
        </div>
      </div>
    </main>
  );
}
